import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase-config';

const LeadForm = ({ model, formType = 'quote', onSuccess, onClose, className = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    selectedModel: model?.title || '',
    gdprAccepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'A név megadása kötelező';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Az email cím megadása kötelező';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Érvényes email címet adjon meg';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'A telefonszám megadása kötelező';
    } else if (!/^(\+36|06)?[1-9][0-9]{7,8}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Érvényes magyar telefonszámot adjon meg';
    }

    if (!formData.gdprAccepted) {
      newErrors.gdprAccepted = 'Az adatvédelmi tájékoztató elfogadása kötelező';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Firebase-be mentés
      await addDoc(collection(db, 'leads'), {
        ...formData,
        formType,
        modelSlug: model?.slug || '',
        createdAt: serverTimestamp(),
        status: 'new',
        source: 'kesz-hazak'
      });

      // Sikeres küldés
      if (onSuccess) {
        onSuccess();
      } else {
        alert('Köszönjük! Üzenetét sikeresen elküldtük. Hamarosan felvesszük Önnel a kapcsolatot.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          selectedModel: model?.title || '',
          gdprAccepted: false
        });
      }
    } catch (error) {
      console.error('Hiba az űrlap küldésekor:', error);
      setErrors({ submit: 'Hiba történt az üzenet küldésekor. Kérjük, próbálja újra.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Hiba törlése a mezőről
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const getTitle = () => {
    switch (formType) {
      case 'callback':
        return 'Kérek visszahívást';
      case 'consultation':
        return 'Kérek konzultációt';
      default:
        return 'Kérek ajánlatot';
    }
  };

  const getDescription = () => {
    switch (formType) {
      case 'callback':
        return 'Hagyja meg elérhetőségét, és kollégánk hamarosan felhívja!';
      case 'consultation':
        return 'Egyeztessen szakértőinkkel a modellről és lehetőségekről!';
      default:
        return 'Töltse ki az alábbi űrlapot, és személyre szabott ajánlatot küldünk!';
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden ${className}`}>
      <div className="bg-gradient-to-r from-[#e31937] to-[#b40000] p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">{getTitle()}</h2>
            <p className="text-white/90">{getDescription()}</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {/* Kiválasztott modell */}
        {model && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                <img
                  src={`/images/kesz-hazak/${model.hero_images[0]}`}
                  alt={model.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/images/placeholder-house.jpg';
                  }}
                />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{model.title}</h3>
                <p className="text-sm text-gray-600">
                  {model.area_m2.toFixed(2)} m² • {new Intl.NumberFormat('hu-HU', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(model.price_from_eur)} tól
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Név */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Teljes név <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e31937] focus:border-transparent ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Vezetéknév Keresztnév"
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            E-mail cím <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e31937] focus:border-transparent ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="email@example.com"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        {/* Telefon */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Telefonszám <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e31937] focus:border-transparent ${
              errors.phone ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="+36 30 123 4567"
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>

        {/* Üzenet */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Üzenet
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e31937] focus:border-transparent resize-none"
            placeholder="Írja meg kérdéseit, egyedi igényeit..."
          />
        </div>

        {/* GDPR checkbox */}
        <div>
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              name="gdprAccepted"
              checked={formData.gdprAccepted}
              onChange={handleChange}
              className={`mt-1 ${errors.gdprAccepted ? 'border-red-500' : ''}`}
            />
            <span className="text-sm text-gray-600">
              Elolvastam és elfogadom az{' '}
              <a href="/impresszum" target="_blank" className="text-[#e31937] hover:underline">
                ÁSZF-et
              </a>{' '}
              és{' '}
              <a href="/jogi-nyilatkozat" target="_blank" className="text-[#e31937] hover:underline">
                Adatvédelmi tájékoztatót
              </a>
              . <span className="text-red-500">*</span>
            </span>
          </label>
          {errors.gdprAccepted && <p className="text-red-500 text-sm mt-1">{errors.gdprAccepted}</p>}
        </div>

        {/* Submit hiba */}
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-600 text-sm">{errors.submit}</p>
          </div>
        )}

        {/* Submit gomb */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#e31937] hover:bg-[#b40000] disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
        >
          {isSubmitting ? 'Küldés...' : getTitle()}
        </button>

        {/* Disclaimer */}
        <div className="text-xs text-gray-500 text-center">
          A megkeresésekre 3 munkanapon belül válaszolunk. Az adatokat bizalmasan kezeljük.
        </div>
      </form>
    </div>
  );
};

export default LeadForm;
